import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Sparkles, ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { THERABODY_PRODUCTS } from "@/lib/constants";
import { track } from "@/lib/analytics";

// C5 — Therabody product-fit quiz.

const PRODUCT_TAGS = {
  "theragun-mini-plus":      ["recovery", "heat", "travel", "sport", "full_body", "mid_budget"],
  "smartgoggles":            ["sleep", "stress", "head_eyes", "tension_headaches", "premium"],
  "jetboots-prime":          ["recovery", "lower_body", "sport", "travel", "premium"],
  "jetboots-pro-plus":       ["recovery", "lower_body", "sport", "premium", "heat", "vibration"],
  "theragun-mini-gen-3":     ["recovery", "travel", "full_body", "entry_budget"],
  "sleep-mask":              ["sleep", "stress", "head_eyes", "travel", "entry_budget"],
  "theraface-mask-glo":      ["face", "head_eyes", "skin", "premium"],
  "theraface-depuffing-wand":["face", "head_eyes", "skin", "entry_budget"],
  "thermback-led":           ["back_desk", "heat", "premium"],
};

const QUESTIONS = [
  {
    key: "goal",
    title: "What's your primary goal?",
    options: [
      { label: "Post-workout recovery", tags: ["recovery", "sport"] },
      { label: "Sleep & stress relief", tags: ["sleep", "stress", "head_eyes"] },
      { label: "Lower-back / desk-pain support", tags: ["back_desk", "heat"] },
      { label: "Face, skin & at-home routine", tags: ["face", "skin", "head_eyes"] },
      { label: "Travel-friendly maintenance", tags: ["travel", "entry_budget"] },
    ],
  },
  {
    key: "area",
    title: "Body area focus?",
    options: [
      { label: "Full body / wherever I'm sore", tags: ["full_body"] },
      { label: "Legs and feet", tags: ["lower_body"] },
      { label: "Lower back", tags: ["back_desk"] },
      { label: "Head, eyes or face", tags: ["head_eyes", "face"] },
    ],
  },
  {
    key: "heat",
    title: "Does heat help your recovery?",
    options: [
      { label: "Yes — heat feels great", tags: ["heat"] },
      { label: "Sometimes, depending on the area", tags: ["heat"] },
      { label: "Not particularly", tags: [] },
    ],
  },
  {
    key: "budget",
    title: "Investment range?",
    options: [
      { label: "Under US $300 — entry", tags: ["entry_budget"] },
      { label: "US $300 – $700 — mid", tags: ["mid_budget"] },
      { label: "US $700+ — premium", tags: ["premium"] },
      { label: "No preference", tags: [] },
    ],
  },
];

function scoreProducts(tags) {
  return THERABODY_PRODUCTS.map((p) => {
    const profile = PRODUCT_TAGS[p.slug] || [];
    return { ...p, score: tags.filter((x) => profile.includes(x)).length };
  }).sort((a, b) => b.score - a.score);
}

export default function TherabodyMatch() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);

  const q = QUESTIONS[step];
  const pick = (option) => {
    const next = { ...answers, [q.key]: option };
    setAnswers(next);
    if (step + 1 < QUESTIONS.length) setStep(step + 1);
    else {
      setDone(true);
      track("therabody_match_complete");
    }
  };
  const restart = () => { setStep(0); setAnswers({}); setDone(false); };

  return (
    <main data-testid="therabody-match-page" className="bg-white min-h-screen">
      <SEO
        title="Therabody product match — Ashlan Clinic"
        description="A short quiz to help you choose the Therabody product that fits your goals — recovery, sleep, posture or facial care."
        path="/therabody/match"
        noIndex
      />
      <section className="border-b border-[#e5e7eb] bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-10">
          <span className="ash-eyebrow">Therabody · Product Match</span>
          <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-[#080808]">
            Four questions. Two product picks.
          </h1>
          <p className="mt-6 text-lg text-[#3C4048] leading-relaxed">
            Built from the Therabody catalogue we actually stock and prescribe at the clinic. Final fit confirmed by your therapist.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {!done ? (
            <div data-testid="tb-step" className="border border-[#e5e7eb] bg-white p-8 md:p-12">
              <div className="flex items-center justify-between mb-6">
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3A7A8C]">Step {step + 1} of {QUESTIONS.length}</span>
                <div className="h-1 flex-1 mx-6 bg-[#F5F5F5] overflow-hidden">
                  <div className="h-full bg-[#5FA8B4] transition-all"
                    style={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }} />
                </div>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#080808] tracking-tight">{q.title}</h2>
              <div className="mt-8 grid gap-3">
                {q.options.map((opt) => (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => pick(opt)}
                    data-testid={`tb-opt-${q.key}-${opt.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="text-left p-5 border border-[#e5e7eb] hover:border-[#3A7A8C] hover:bg-[#F0F7F9] text-[#080808] font-semibold transition-colors flex items-center gap-3"
                  >
                    <span className="w-2 h-2 rounded-full bg-[#5FA8B4]" /> {opt.label}
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button type="button" onClick={() => setStep(step - 1)} className="mt-8 ash-link-underline inline-flex items-center gap-1.5">
                  <ArrowLeft size={14} /> Back
                </button>
              )}
            </div>
          ) : (
            <TherabodyResult answers={answers} restart={restart} />
          )}
        </div>
      </section>
    </main>
  );
}

function TherabodyResult({ answers, restart }) {
  const tags = Object.values(answers).flatMap((a) => a?.tags || []);
  const ranked = scoreProducts(tags).filter((p) => p.score > 0);
  const top = ranked[0] || scoreProducts(tags)[0];
  const second = ranked[1];

  return (
    <div data-testid="tb-result" className="space-y-6">
      <div className="border border-[#3A7A8C] bg-[#F0F7F9] p-6 md:p-8">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={16} className="text-[#3A7A8C]" />
          <span className="ash-eyebrow">Your top match</span>
        </div>
        <ProductCard product={top} primary />
      </div>
      {second && (
        <div className="border border-[#e5e7eb] bg-white p-6 md:p-8" data-testid="tb-second">
          <span className="ash-eyebrow">Also worth a look</span>
          <div className="mt-2"><ProductCard product={second} /></div>
        </div>
      )}
      <div className="flex flex-wrap gap-3 pt-4">
        <Link to="/therabody" className="ash-btn-secondary" data-testid="tb-see-all">
          See the full catalogue <ArrowRight size={16} />
        </Link>
        <button type="button" onClick={restart} data-testid="tb-restart" className="ash-link-underline ml-auto">
          Retake the quiz
        </button>
      </div>
    </div>
  );
}

function ProductCard({ product, primary }) {
  return (
    <div className="grid sm:grid-cols-[140px_1fr] gap-5 items-start">
      <div className="aspect-square bg-[#F5F5F5] overflow-hidden">
        <img src={product.image} alt={product.name} className="w-full h-full object-contain p-3" loading="lazy" />
      </div>
      <div>
        <h3 className={`font-bold text-[#080808] leading-tight ${primary ? "text-2xl md:text-3xl" : "text-xl"}`}>
          {product.name}
        </h3>
        <p className="mt-2 text-sm text-[#3C4048] leading-relaxed line-clamp-3">{product.description}</p>
        <Link
          to={`/therabody#${product.slug}`}
          data-testid={`tb-cta-${product.slug}`}
          className="mt-4 ash-link-underline inline-flex items-center gap-1.5"
        >
          See on Therabody page <ArrowUpRight size={14} />
        </Link>
      </div>
    </div>
  );
}
